import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  broadcastScripts,
  type ScriptsBroadcastRequest,
  type ScriptsBroadcastResponse,
} from "@/features/persona-opportunities/api/scripts-broadcast.api";
import i18n from "@/lib/i18n";
import { Sentry } from "@/lib/sentry";

import { revealGenerationTask } from "./opportunity-sets.queries";

export interface ScriptsBroadcastSummary {
  /** Script jobs the BFF published, across every tenant. */
  scriptCount: number;
  /** Scripts whose publish timed out: not confirmed, and they may still start. */
  pendingScriptCount: number;
  /** Scripts that did not start, on tenants where something else did. */
  failedScriptCount: number;
  /** Tenants that got a set and at least one started or pending job. */
  tenantCount: number;
  /** Names of tenants where nothing started. */
  failedTenantNames: string[];
  /** The BFF returned an earlier request's run with this key instead of starting one. */
  alreadyRunning: boolean;
}

export function summarizeScriptsBroadcast(
  result: ScriptsBroadcastResponse,
): ScriptsBroadcastSummary {
  let scriptCount = 0;
  let pendingScriptCount = 0;
  let failedScriptCount = 0;
  let tenantCount = 0;
  const failedTenantNames: string[] = [];
  const alreadyRunning = result.state === "in_progress";
  for (const tenant of result.tenants) {
    const started = tenant.jobs.filter((job) => job.status === "started").length;
    const pending = tenant.jobs.filter((job) => job.status === "pending").length;
    const failed = tenant.jobs.filter((job) => job.status === "failed").length;
    scriptCount += started;
    pendingScriptCount += pending;
    failedScriptCount += failed;
    if (started + pending > 0) tenantCount += 1;
    // A recorded tenant of an earlier run may not have its jobs listed yet; it did not fail.
    else if (!(alreadyRunning && tenant.setId && !tenant.error)) {
      failedTenantNames.push(tenant.tenantName || tenant.tenantId);
    }
  }
  return { scriptCount, pendingScriptCount, failedScriptCount, tenantCount, failedTenantNames, alreadyRunning };
}

/** Nothing started, nothing may still start, and no earlier run with this key: the dialog stays open. */
export function broadcastStartedNothing(summary: ScriptsBroadcastSummary): boolean {
  return summary.scriptCount === 0 && summary.pendingScriptCount === 0 && !summary.alreadyRunning;
}

function notifyBroadcastResult(summary: ScriptsBroadcastSummary): void {
  if (summary.alreadyRunning) {
    toast.info(
      i18n.t(
        "personaOpportunities.scripts.broadcast.alreadyRunningToast",
        "This run already started — watch the task panel",
      ),
    );
    return;
  }
  if (summary.scriptCount > 0) {
    toast.success(
      i18n.t("personaOpportunities.scripts.broadcast.startedToast", {
        count: summary.scriptCount,
        tenants: i18n.t("personaOpportunities.scripts.broadcast.tenantCount", {
          count: summary.tenantCount,
          defaultValue_one: "{{count}} tenant",
          defaultValue_other: "{{count}} tenants",
        }),
        defaultValue_one: "Started {{count}} script on {{tenants}} — watch the task panel",
        defaultValue_other: "Started {{count}} scripts on {{tenants}} — watch the task panel",
      }),
    );
  }
  if (summary.pendingScriptCount > 0) {
    toast.info(
      i18n.t("personaOpportunities.scripts.broadcast.pendingToast", {
        count: summary.pendingScriptCount,
        defaultValue_one: "{{count}} script is not confirmed yet and may still start — watch the task panel",
        defaultValue_other: "{{count}} scripts are not confirmed yet and may still start — watch the task panel",
      }),
    );
  }
  if (summary.scriptCount + summary.pendingScriptCount > 0 && summary.failedScriptCount > 0) {
    toast.error(
      i18n.t("personaOpportunities.scripts.broadcast.partialToast", {
        count: summary.failedScriptCount,
        defaultValue_one: "{{count}} script could not start",
        defaultValue_other: "{{count}} scripts could not start",
      }),
    );
  }
  // When nothing started the dialog stays open and names the tenants itself.
  if (summary.scriptCount + summary.pendingScriptCount > 0 && summary.failedTenantNames.length > 0) {
    toast.error(
      i18n.t("personaOpportunities.scripts.broadcast.failedToast", {
        count: summary.failedTenantNames.length,
        names: summary.failedTenantNames.join(", "),
        defaultValue_one: "Could not start on {{count}} tenant: {{names}}",
        defaultValue_other: "Could not start on {{count}} tenants: {{names}}",
      }),
    );
  }
}

/** `POST /scripts-broadcast` — Generate for tenants on the Creative Team tenant. */
export function useBroadcastScripts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ScriptsBroadcastRequest) => broadcastScripts(body),
    onSuccess: (result) => {
      const summary = summarizeScriptsBroadcast(result);
      // One task card per tenant; refetch so the cards show without the discovery poll.
      if (!broadcastStartedNothing(summary)) revealGenerationTask(queryClient);
      notifyBroadcastResult(summary);
    },
    onError: (err) => {
      Sentry.captureException(err, { tags: { flow: "scripts-broadcast" } });
    },
    // The dialog shows request errors inline (for example a script over the size cap).
    meta: { skipGlobalError: true },
  });
}
